import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchTrainingsDashboard,
  deleteTraining,
  updateTraining
} from '../../../../api/adminTrainingApi';
import {
  fetchTrainingVideosByTrainingId,
  updateTrainingVideo
} from '../../../../api/trainingVideoApi';
import Modal from 'react-modal';
import { FaEdit, FaTrash, FaTimes, FaEye, FaChevronDown, FaVideo } from 'react-icons/fa';
import { MdZoomIn, MdZoomOut } from 'react-icons/md';
import { toast } from 'react-toastify';
import ReactPlayer from 'react-player';
import './TrainingHistory.css';
import CommonDataTable from '../../../../components/CustomTable/CustomTable';

Modal.setAppElement('#root');

const TrainingHistory = () => {
  const [trainings, setTrainings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);

  const [isContentListModalOpen, setIsContentListModalOpen] = useState(false);
  const [trainingVideos, setTrainingVideos] = useState([]);
  const [editingVideo, setEditingVideo] = useState(null);
  const [expandedVideoDescId, setExpandedVideoDescId] = useState(null);
  const [expandedVideoDrawerId, setExpandedVideoDrawerId] = useState(null);

  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfScale, setPdfScale] = useState(1);
  const [pdfPage, setPdfPage] = useState(1);
  const [videoTitle, setVideoTitle] = useState('');
  const [pdfList, setPdfList] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  useEffect(() => {
    document.title = 'Training History - Finhaat Gurukul';
  }, []);

  const handlePdfZoomIn = () => setPdfScale(prev => Math.min(prev + 0.1, 3));
  const handlePdfZoomOut = () => setPdfScale(prev => Math.max(prev - 0.1, 0.5));

  // Exactly the same styles, except we add overflow to enable scrolling
  const contentListModalStyles = {
    content: {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '80%',
      overflowY: 'auto',
    },
    overlay: {
      backgroundColor: 'rgba(0,0,0,0.5) !important'
    }
  };


  const pdfModalStyles = {
    content: {
      position: 'static',
      top: '0',
      right: '0',
      bottom: '0',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '0',
      border: 'none',
      boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.2)',
      overflow: 'hidden',
      height: '100%',
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
    },
  };

  const loadTrainings = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        search: 'isDeleted:eq:0',
        sortBy: 'trainingName',
        sortDirection: 'asc',
        page: 0,
        size: 1000
      };
      const data = await fetchTrainingsDashboard(params);
      setTrainings(data.content);
    } catch (error) {
      console.error('Error fetching trainings:', error);
      toast.error('Oops! Finhaat hit a data snag. Try again soon!');
      setTrainings([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrainings();
  }, [loadTrainings]);

  // ======== TRAINING CRUD ========
  const handleEditTraining = (training) => {
    setSelectedTraining(training);
    setIsModalOpen(true);
  };

  const handleUpdateTraining = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const updatedTraining = {
        ...selectedTraining,
        trainingName: e.target.trainingName.value,
        trainingDescription: e.target.trainingDescription.value,
        trainingDuration: parseInt(e.target.trainingDuration.value, 10),
      };
      await updateTraining(updatedTraining);
      toast.success('Training updated successfully!');
      handleCloseModal();
      await loadTrainings();
    } catch (error) {
      console.error('Error updating training:', error);
      toast.error('Failed to update training. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTraining = async (trainingId) => {
    if (window.confirm('Are you sure you want to delete this training?')) {
      setIsLoading(true);
      try {
        await deleteTraining(trainingId);
        await loadTrainings();
        toast.success('Training deleted successfully!', {
          icon: '🗑️'
        });
      } catch (error) {
        console.error('Error deleting training:', error);
        toast.error('Oops! Finhaat hit a data snag. Try again soon!', {
          icon: '❌'
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTraining(null);
  };

  // ======== CONTENT LIST (PDF + VIDEO) ========
  const handleViewContent = async (training) => {
    setSelectedTraining(training);
    setHighlightedRowId(training.trainingId);
    try {
      const response = await fetchTrainingVideosByTrainingId(training.trainingId);
      const items = Array.isArray(response) ? response : [];
      setTrainingVideos(items);
      setIsContentListModalOpen(true);
    } catch (error) {
      console.error('Error fetching training videos:', error);
      toast.error('Failed to fetch training videos. Please try again.');
    }
  };

  // For each item in the “Content Items” list:
  const handleViewSingleContent = (item) => {
    if (item.contentType === 'PDF') {

      const allPdfs = trainingVideos.filter(v => v.contentType === 'PDF');
      setPdfList(allPdfs);
      setVideoTitle(item.videoTitle);
      setPdfUrl(item.videoUrl);
      setPdfPage(1);
      setIsPdfModalOpen(true);
    } else {
      setExpandedVideoDrawerId(prev =>
        prev === item.trainingVideoId ? null : item.trainingVideoId
      );
      setEditingVideo(null);
    }
  };

  const handleCloseContentListModal = () => {
    setIsContentListModalOpen(false);
    setTrainingVideos([]);
    setExpandedVideoDrawerId(null);
  };

  // ======== VIDEO DRAWER LOGIC =======
  const toggleDescription = (videoId) => {
    setExpandedVideoDescId(prev => (prev === videoId ? null : videoId));
  };

  const handleStartEditVideo = (video) => {
    setEditingVideo({ ...video, isEditing: true });
  };

  const handleCancelEditVideo = () => {
    setEditingVideo(null);
  };

  const handleUpdateVideo = async (e) => {
    e.preventDefault();
    const updatedVideo = {
      trainingVideoId: editingVideo.trainingVideoId,
      trainingId: editingVideo.trainingId,
      videoSequenceId: editingVideo.videoSequenceId,
      videoThumbnailUrl: e.target.videoThumbnailUrl.value,
      videoUrl: e.target.videoUrl.value,
      videoDesc: e.target.videoDesc.value,
      videoDuration: parseInt(e.target.videoDuration.value, 10),
      mandatoryDuration: parseInt(e.target.mandatoryDuration.value, 10),
      videoTitle: e.target.videoTitle.value,
      examType: editingVideo.examType,    
      category: editingVideo.category,     
      contentType: editingVideo.contentType, 
      language: editingVideo.language,        
    };
    

    try {
      await updateTrainingVideo(updatedVideo);
      toast.success('Video updated successfully!');
      setEditingVideo(null);
      const refreshedVideos = await fetchTrainingVideosByTrainingId(
        updatedVideo.trainingId
      );
      setTrainingVideos(Array.isArray(refreshedVideos) ? refreshedVideos : []);
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error('Failed to update the video. Please try again.');
    }
  };

  // ======== DATATABLE & SEARCH ========
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const filteredTrainings = useMemo(() => {
    return trainings.filter(training =>
      training.trainingName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      training.trainingDescription.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [trainings, searchTerm]);

  const paginatedTrainings = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredTrainings.slice(start, start + perPage);
  }, [filteredTrainings, currentPage, perPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const columns = [
    {
      name: 'Training Name',
      selector: row => row.trainingName,
      sortable: true,
      cell: row => (
        <div
          style={{
            backgroundColor: row.trainingId === highlightedRowId ? '#FAC7C7' : 'transparent',
            color: row.trainingId === highlightedRowId ? '#FF0064' : 'inherit',
            width: '100%',
            minHeight: '45px',
            padding: '8px',
            borderRadius: '5px',
            fontWeight: '550',
          }}
        >
          {row.trainingName}
        </div>
      ),
    },
    {
      name: 'Description',
      selector: row => row.trainingDescription,
      sortable: true,
      cell: row => (
        <div
          style={{
            backgroundColor: row.trainingId === highlightedRowId ? '#FAC7C7' : 'transparent',
            color: row.trainingId === highlightedRowId ? '#FF0064' : 'inherit',
            width: '100%',
            minHeight: '45px',
            padding: '8px',
            borderRadius: '5px',
            fontWeight: '550',
          }}
        >
          {row.trainingDescription}
        </div>
      ),
    },
    {
      name: 'Duration (mins)',
      selector: row => row.trainingDuration,
      sortable: true,
      cell: row => (
        <div
          style={{
            backgroundColor: row.trainingId === highlightedRowId ? '#FAC7C7' : 'transparent',
            color: row.trainingId === highlightedRowId ? '#FF0064' : 'inherit',
            width: '100%',
            minHeight: '45px',
            padding: '8px',
            borderRadius: '5px',
            fontWeight: '550',
          }}
        >
          {row.trainingDuration}
        </div>
      ),
    },
    {
      name: 'Videos / PDF',
      cell: row => (
        <div
          style={{
            backgroundColor: row.trainingId === highlightedRowId ? '#FAC7C7' : 'transparent',
            color: row.trainingId === highlightedRowId ? '#FF0064' : 'inherit',
            width: '100%',
            minHeight: '45px',
            padding: '8px',
            borderRadius: '5px',
            fontWeight: '550',
          }}
        >
          <button
            onClick={() => handleViewContent(row)}
            className="action-btn view-btn-table-modal"
          >
            <FaEye />
          </button>
        </div>
      ),
    },
    {
      name: 'Actions',
      cell: row => (
        <div
          style={{
            backgroundColor: row.trainingId === highlightedRowId ? '#FAC7C7' : 'transparent',
            color: row.trainingId === highlightedRowId ? '#FF0064' : 'inherit',
            width: '100%',
            minHeight: '45px',
            padding: '8px',
            borderRadius: '5px',
            fontWeight: '550',
          }}
        >
          <button onClick={() => handleEditTraining(row)} className="action-btn edit-btn">
            <FaEdit />
          </button>
          <button
            onClick={() => handleDeleteTraining(row.trainingId)}
            className="action-btn delete-btn"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="training-history">
      <div className="header">
        <h2 className="header-training-history">Training History</h2>
      </div>
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by Training Name or Description..."
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>
      <CommonDataTable
        columns={columns}
        data={paginatedTrainings}
        totalRows={filteredTrainings.length}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        isLoading={isLoading}
        currentPage={currentPage}
        perPage={perPage}
      />

      {/* ===== EDIT TRAINING MODAL ===== */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        overlayClassName="modal-overlay"
        className="modal-content"
      >
        <div className="modal-header">
          <h2>Edit Training</h2>
          <button onClick={handleCloseModal} className="modal-close-btn">
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleUpdateTraining}>
          <div className="modal-body">
            <label htmlFor="trainingName">Training Name</label>
            <input
              type="text"
              id="trainingName"
              name="trainingName"
              defaultValue={selectedTraining?.trainingName || ''}
              required
            />

            <label htmlFor="trainingDescription">Training Description</label>
            <textarea
              id="trainingDescription"
              name="trainingDescription"
              defaultValue={selectedTraining?.trainingDescription || ''}
              rows="4"
              required
            />

            <label htmlFor="trainingDuration">Training Duration (mins)</label>
            <input
              type="number"
              id="trainingDuration"
              name="trainingDuration"
              defaultValue={selectedTraining?.trainingDuration || ''}
              required
            />
          </div>

          <div className="modal-footer">
            <button type="submit">Update</button>
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* ===== CONTENT LIST MODAL (PDF + VIDEO DRAWERS) ===== */}
      <Modal
        isOpen={isContentListModalOpen}
        onRequestClose={handleCloseContentListModal}
        contentLabel="Training Content Modal"
        overlayClassName="video-modal-overlay"
        className="video-modal"
        style={{ content: { overflowY: 'auto' } }}
      >

        <button
          onClick={handleCloseContentListModal}
          className="modal-close-btn-table-modal-1"
        >
          <FaTimes />
        </button>
        <h3 className="content-list-title">
          {selectedTraining?.trainingName} - Content Items
        </h3>

        <div className="content-list-container">
          {trainingVideos.map((item, index) => (
            <div key={item.trainingVideoId} className="content-item-card"
              onClick={() => handleViewSingleContent(item)} >
              <div className="content-item-header">
                <div className="content-item-title">
                  {index + 1}. {item.videoTitle}
                  <span className="content-type-badge">
                    {item.contentType === 'PDF' ? 'PDF' : 'Video'}
                  </span>
                </div>
                <button
                  className="content-item-view-btn"

                >
                  <FaChevronDown
                    style={{
                      transform: expandedVideoDrawerId === item.trainingVideoId ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                </button>

              </div>

              {/* {item.videoDesc && (
                <p className="content-item-desc">
                  {item.videoDesc.length > 50
                    ? item.videoDesc.substring(0, 50) + '...'
                    : item.videoDesc}
                </p>
              )} */}


              {expandedVideoDrawerId === item.trainingVideoId && item.contentType !== 'PDF' && (
                <div className="video-drawer">
                  {!editingVideo?.isEditing || editingVideo?.trainingVideoId !== item.trainingVideoId ? (
                    <div className="video-card">
                      <div className="video-player-wrapper" onClick={(e) => e.stopPropagation()}>
                        <ReactPlayer
                          url={item.videoUrl}
                          controls
                          width="100%"
                          height="100%"
                          light={item.videoThumbnailUrl ? item.videoThumbnailUrl : true}
                        />
                      </div>


                      <div className="video-card-content">
                        <h4 className="video-title">{item.videoTitle}</h4>
                        <div className="video-meta">
                          <span>Video: {item.videoDuration} mins</span>
                          <span>Mandatory: {item.mandatoryDuration} mins</span>
                        </div>
                        <div className="video-description">
                          <div
                            className="description-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDescription(item.trainingVideoId);
                            }}
                          >
                            Description
                            <span style={{ marginLeft: 5 }}>
                              {expandedVideoDescId === item.trainingVideoId ? '▲' : '▼'}
                            </span>
                          </div>

                          {expandedVideoDescId === item.trainingVideoId && (
                            <p className="full-description">{item.videoDesc}</p>
                          )}
                        </div>
                        <div className="video-card-footer">
                          {/* Edit button */}
                          <button
                            className="edit-btn-for-video"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditVideo(item);
                            }}
                          >
                            Edit
                          </button>

                        </div>
                      </div>
                    </div>
                  ) : (

                    <form 
                    onClick={(e) => e.stopPropagation()}
                    onSubmit={handleUpdateVideo} className="edit-video-form">
                      <h4>Edit Video</h4>
                      <button
                        type="button"
                        className="close-edit-form"
                        onClick={handleCancelEditVideo}
                      >
                        ✕
                      </button>

                      <label>Video Title</label>
                      <input
                        type="text"
                        name="videoTitle"
                        defaultValue={editingVideo.videoTitle}
                        required
                      />

                      <label>Description</label>
                      <textarea
                        name="videoDesc"
                        rows={3}
                        defaultValue={editingVideo.videoDesc}
                        required
                      />

                      <label>Video URL</label>
                      <input
                        type="text"
                        name="videoUrl"
                        defaultValue={editingVideo.videoUrl}
                        required
                      />

                      <label>Thumbnail URL</label>
                      <input
                        type="text"
                        name="videoThumbnailUrl"
                        defaultValue={editingVideo.videoThumbnailUrl}
                        required
                      />

                      <label>Duration (mins)</label>
                      <input
                        type="number"
                        name="videoDuration"
                        defaultValue={editingVideo.videoDuration}
                        required
                      />

                      <label>Mandatory Duration (mins)</label>
                      <input
                        type="number"
                        name="mandatoryDuration"
                        defaultValue={editingVideo.mandatoryDuration}
                        required
                      />

                      <div className="form-btns">
                        <button type="submit" className="update-btn">
                          Update
                        </button>
                        <button
                          type="button"
                          className="close-btn"
                          onClick={handleCancelEditVideo}
                        >
                          Close
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Modal>

      {/* ===== PDF MODAL ===== */}
      <Modal
        isOpen={isPdfModalOpen}
        onRequestClose={() => setIsPdfModalOpen(false)}
        style={pdfModalStyles}
        overlayClassName="video-modal-overlay"
        contentLabel="PDF Modal"
      >
        {/* Header Section */}
        <div className="pdf-modal-header">
          <select
            value={videoTitle}
            onChange={(e) => {
              const selectedPdf = pdfList.find(
                pdf => pdf.videoTitle === e.target.value
              );
              if (selectedPdf) {
                setVideoTitle(selectedPdf.videoTitle);
                setPdfUrl(selectedPdf.videoUrl);
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              margin: 0,
              fontSize: '1.25rem',
              fontWeight: 'bold',
              border: 'none',
              background: 'transparent',
              color: '#333'
            }}
          >
            {pdfList.map(pdf => (
              <option key={pdf.trainingVideoId} value={pdf.videoTitle}>
                {pdf.videoTitle}
              </option>
            ))}
          </select>
          <button
            onClick={() => setIsPdfModalOpen(false)}
            className="pdf-modal-close-btn"
          >
            <FaTimes />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="pdf-modal-body">
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <iframe
              src={`${pdfUrl}#toolbar=0&page=${pdfPage}`}
              title="PDF Document"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${100 / pdfScale}%`,
                height: `${100 / pdfScale}%`,
                border: 'none',
                transform: `scale(${pdfScale})`,
                transformOrigin: 'top left'
              }}
            />
          </div>
        </div>

        {/* Footer Section with Zoom */}
        <div className="pdf-modal-footer">
          <button onClick={handlePdfZoomOut} className="zoom-btn">
            <MdZoomOut size={20} />
          </button>
          <span className="zoom-level">{Math.round(pdfScale * 100)}%</span>
          <button onClick={handlePdfZoomIn} className="zoom-btn">
            <MdZoomIn size={20} />
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TrainingHistory;
